import { BOTTOM_BASE_IN_MM, EIGHTANDHALF_FEET_IN_MM } from "./consts";
import { BayLevelEnum, ISizeSummary } from "open-vessel-definition";
import { pad2, sortNumericAsc } from "@tedivo/tedivo-pure-helpers";

import { BaySizesAndCgs } from "./BaySizesAndCgs";
import { IGetPairedBaysOutput } from "../getAllThePairedBays";
import { ITierPattern } from "../types/IPositionPatterns";
import { createTiersFromConfig } from "../createTiersFromConfig";

export function calculateMissingBottomBases(
  missingImportantVcgs: boolean,
  sizeSummary: ISizeSummary,
  pairedBaysCalc: IGetPairedBaysOutput,
  blpsByBayAndLevel: Record<string, BaySizesAndCgs>,
): {
  missingBottomBases: Record<ITierPattern, number>;
  tiersBoundaries: IMostObservedTiersAboveAndBelow;
} {
  const missingBottomBases: Record<ITierPattern, number> = {};
  const tiersBoundaries = getMostObservedTiersAboveAndBelow();

  if (!missingImportantVcgs) return { missingBottomBases, tiersBoundaries };

  const tiersBelow = createTiersFromConfig(
    tiersBoundaries.maxNumberOfTiersBelow.lowestTierBelow,
    tiersBoundaries.maxNumberOfTiersBelow.highestTierBelow,
    true,
  );

  const tiersAbove = createTiersFromConfig(
    sizeSummary.minAboveTier,
    sizeSummary.maxAboveTier,
    true,
  );

  tiersBelow.forEach((t, idx) => {
    missingBottomBases[pad2(t)] = BOTTOM_BASE_IN_MM + idx * EIGHTANDHALF_FEET_IN_MM;
  });

  const maxBottomBaseFromBelow =
    missingBottomBases[pad2(tiersBoundaries.maxNumberOfTiersBelow.highestTierBelow)] || 0;

  const maxHeightFromBottomBase = maxBottomBaseFromBelow
    ? maxBottomBaseFromBelow + EIGHTANDHALF_FEET_IN_MM
    : BOTTOM_BASE_IN_MM;

  const aboveBaseBottom = maxHeightFromBottomBase + 1000;

  tiersAbove.forEach((t, idx) => {
    missingBottomBases[pad2(t)] = aboveBaseBottom + idx * EIGHTANDHALF_FEET_IN_MM;
  });

  return {
    missingBottomBases,
    tiersBoundaries,
  };

  function getMostObservedTiersAboveAndBelow(): IMostObservedTiersAboveAndBelow {
    const minTiersAboveStats: Record<number, number> = {};
    const maxTiersBelowStats: Record<number, number> = {};

    const belowTiers = createTiersFromConfig(sizeSummary.minBelowTier, sizeSummary.maxBelowTier);

    let maxNumberOfTiersBelow = 0;
    let lowestTierBelow = Infinity;
    let highestTierBelow = -Infinity;

    // Make the calculation taking into account paired bays
    [...pairedBaysCalc.pairedBays, ...pairedBaysCalc.unpairedBays].forEach((pb) => {
      if (pb.level === BayLevelEnum.ABOVE) {
        // 0.1 Calculate Most observed lowest tier above
        let lowestTier = Infinity;

        pb.allBays.forEach((b) => {
          const blp = blpsByBayAndLevel[`${b}-${BayLevelEnum.ABOVE}`];
          if (blp.minTier < lowestTier) lowestTier = blp.minTier;
        });

        // 0.1.1 Count the number of times the lowest tier above is observed
        minTiersAboveStats[lowestTier] = (minTiersAboveStats[lowestTier] || 0) + 1;
      } else if (pb.level === BayLevelEnum.BELOW) {
        // 0.2 Calculate Most observed highest tier below
        let highestTier = -Infinity;

        pb.allBays.forEach((b) => {
          const blp = blpsByBayAndLevel[`${b}-${BayLevelEnum.BELOW}`];
          if (blp.maxTier > highestTier) highestTier = blp.maxTier;

          const numberOfTiers = Math.floor((blp.maxTier - blp.minTier) * 0.5) + 1;

          if (numberOfTiers > maxNumberOfTiersBelow) {
            maxNumberOfTiersBelow = numberOfTiers;

            if (blp.minTier < lowestTierBelow) lowestTierBelow = blp.minTier;
            if (blp.maxTier > highestTierBelow) highestTierBelow = blp.maxTier;
          }

          maxNumberOfTiersBelow = Math.max(maxNumberOfTiersBelow, numberOfTiers);
        });

        // 0.2.1 Count the number of times the highest tier below is observed
        maxTiersBelowStats[highestTier] = (maxTiersBelowStats[highestTier] || 0) + 1;
      }
    });

    const mostObservedLowestTierAboveCount = Math.max(...Object.values(minTiersAboveStats));
    const mostObservedHighestTierBelowCount = Math.max(...Object.values(maxTiersBelowStats));

    const mostObservedLowestTierAbove = Object.keys(minTiersAboveStats)
      .map(Number)
      .find((t) => minTiersAboveStats[t] === mostObservedLowestTierAboveCount);

    const mostObservedHighestTierBelow = Object.keys(maxTiersBelowStats)
      .map(Number)
      .find((t) => maxTiersBelowStats[t] === mostObservedHighestTierBelowCount);

    const overallLowestTierBelow = belowTiers.map(Number).sort(sortNumericAsc)[0];

    if (lowestTierBelow > overallLowestTierBelow) {
      lowestTierBelow = overallLowestTierBelow;
      maxNumberOfTiersBelow = (highestTierBelow - lowestTierBelow) / 2 + 1;
    }

    return {
      mostObservedLowestTierAbove,
      mostObservedHighestTierBelow,
      maxNumberOfTiersBelow: {
        lowestTierBelow,
        highestTierBelow,
        n: maxNumberOfTiersBelow,
      },
    };
  }
}

export interface IMostObservedTiersAboveAndBelow {
  /** The above TIER (i.e. 80) as number */
  mostObservedLowestTierAbove: number | undefined;
  /** The below TIER (i.e. 18) as number */
  mostObservedHighestTierBelow: number | undefined;
  /** Stats about the tiers below */
  maxNumberOfTiersBelow: {
    lowestTierBelow: number;
    highestTierBelow: number;
    n: number;
  };
}
